import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { DEFAULT_LOCALE } from '../../config/locales';

const Wrapper = styled.div`
  position: relative;
  display: block;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.cream};
  border: 1px solid rgba(245, 242, 235, 0.25);
  padding: 0.65rem 0.8rem;
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
`;

const FlagIcon = styled.img`
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 2px;
`;

const TriggerLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const ChevronIcon = styled(ChevronDown)`
  transition: transform 0.2s;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Dropdown = styled.div`
  position: absolute;
  top: auto;
  bottom: calc(100% + 0.35rem);
  left: 0;
  right: 0;
  transform: none;
  width: 100%;
  background: #fff;
  color: #111;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-height: 320px;
  overflow-y: auto;
  z-index: 20;
  padding: 0.4rem;
`;

const Option = styled.button`
  width: 100%;
  border: 0;
  background: ${(p) => (p.$active ? '#f2f2f2' : 'transparent')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-align: left;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  &:hover {
    background: #f4f4f4;
  }
`;

const getFlagUrl = (flagCode) =>
  flagCode ? `https://flagcdn.com/${flagCode}.svg` : 'https://flagcdn.com/w320/un.png';

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { locale, setLocale, locales, getPathWithoutLocale } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, []);

  const handleSelect = (loc) => {
    setLocale(loc.code);
    const base = getPathWithoutLocale(location.pathname);
    const path = loc.code === DEFAULT_LOCALE ? base : `/${loc.urlCode}${base === '/' ? '' : base}`;
    navigate(path || '/');
    setOpen(false);
  };

  const current = locales.find((l) => l.code === locale) || locales[0];
  return (
    <Wrapper ref={ref}>
      <Trigger
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('maison.switchLanguage')}
        aria-expanded={open}
      >
        <FlagIcon src={getFlagUrl(current?.flagCode)} alt="" />
        <TriggerLabel>{current?.country || current?.name}</TriggerLabel>
        <ChevronIcon size={14} $open={open} />
      </Trigger>
      {open && (
        <Dropdown>
          {locales.map((loc) => (
            <Option
              key={loc.code}
              type="button"
              $active={loc.code === locale}
              onClick={() => handleSelect(loc)}
            >
              <FlagIcon src={getFlagUrl(loc.flagCode)} alt="" />
              <span>{loc.country}</span>
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
}

